import { useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { getLocalTimeZone, parseZonedDateTime } from '@internationalized/date';
import { uniq } from 'lodash-es';
import { IoClose } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { InfoBanner, LinkToCase } from '@ui';
import { AircraftIcon, CarGroundIcon, LightsAndSirens } from '@icons';
import { Button, Input, TmdxAvatar, TmdxChip } from '@ds';
import { useAuthContext } from '@lp/core/auth';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useResourcesList } from '@lp/features/RoutePlanner/hooks/useResourcesList';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useConvertSuggestedRoutes } from '@lp/features/RoutePlanner/components/SuggestedRoutes/hooks';
import { Case, CaseResourceType, OrganType, QuoteRejectReason, QuoteStatus } from '@/api/apis/case';
import { QuoteApproveInternallyBody, RouteQuote } from '@/api/apis/quote';
import {
  AircraftLegResource,
  BaseLegResourceType,
  RouteTypeEnum,
  HubRouteLocation,
  RouteLegTypeEnum,
  LogisticsProjectionResponse,
} from '@/api/apis/route';
import { retrieveUnifiedError } from '@/api/utils';
import { RouteBuilderPermissions } from '@/permissions';
import {
  formatDate,
  getCurrencyFormatter,
  getCurrencySymbol,
  getFullName,
  getIsEuropeanRegion,
  getTzDateTimeString,
} from '@/utils/helpers';
import { useBenchmarkById } from '@/queries/route-planner';
import { caseQuoteRejectedReasonNames, quoteStatusNames } from '@/constants/case/case.constants';
import { getRouteLegsDivided } from '@/queries/route-planner/utils';
import { RoutePlanType } from '@/types/route';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { useDeviceTrackingByCaseIdQuery } from '@/queries/device-tracking';
import { HospitalsBlock } from '../CaseInformation/HospitalsBlock';
import { LogisticsResourcesList } from '../CaseInformation/LogisticsResourcesList';
import {
  convertRouteList,
  durationInSecondsToHours,
  getCalculatedAirPriceInCents,
  getRouteSegments,
  parseCurrencyStringUS,
} from '../../utils';
import { RouteSchema, RouteSchemaType } from '../RouteSchema';
import { RouteModalTypes } from '../../types';
import { useRouteModalActions } from '../../hooks/useRouteModals';
import { dangerQuoteStatuses } from './constants';

type QuoteDetailsProps = {
  quoteDetails: RouteQuote;
  logisticsProjectionSnapshot?: LogisticsProjectionResponse;
  isViewOnly?: boolean;
  recallQuote?: boolean;
  desyncedState?: boolean;
  onApprove: (data: QuoteApproveInternallyBody & { id: string }) => Promise<void>;
  onReject: (caseId: string, comment?: string) => Promise<void>;
  onRecall: (caseId: string, comment: string) => Promise<void>;
  onApproveTxCenterProvider?: (caseId: string, comment?: string) => Promise<void>;
  onCloseModal: () => void;
};

export const QuoteDetails = ({
  quoteDetails,
  logisticsProjectionSnapshot,
  isViewOnly,
  recallQuote,
  desyncedState,
  onApprove,
  onReject,
  onRecall,
  onApproveTxCenterProvider,
  onCloseModal,
}: QuoteDetailsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const { checkPermission } = useAuthContext();

  const {
    caseId: id,
    benchmarkId,
    organType,
    donorHospitalDetails: donorHospital,
    txCenterDetails: txCenter,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    txEtaHospitalDate,
    txEtaHospitalTime,
    reAllocationCaseId,
    quote,
    routeComment,
    serviceRegionId,
  } = useCaseDetailsWithReviews() as Case;

  const { data: deviceTracking } = useDeviceTrackingByCaseIdQuery(id, false);

  const mainRouteData = useMemo(() => {
    const mainRoute = logisticsProjectionSnapshot?.routes.find((r) => r.routeType === RouteTypeEnum.Main);

    if (!logisticsProjectionSnapshot || !mainRoute) return null;

    return {
      ...mainRoute,
      ...getRouteLegsDivided(mainRoute.legs, logisticsProjectionSnapshot?.locations),
    };
  }, [logisticsProjectionSnapshot]);

  const { caseLocations, list, getLocationById } = useRouteLocations();
  const { convertRoute } = useConvertSuggestedRoutes();
  const { onOpenModal } = useRouteModalActions();
  const resourcesList = useResourcesList();
  const { Aircraft: aircrafts } = useRouteResources();

  const [comment, setComment] = useState<string | undefined>(quote?.comment?.comment || undefined);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const serverAirQuotePrice = quoteDetails.airQuote?.finalPriceInCents ?? 0;
  const serverGroundQuotePrice = quoteDetails.groundQuote?.limitedPriceInCents || 0;

  const [editedAirQuotePrice, setEditedAirQuotePrice] = useState<number>(serverAirQuotePrice);
  const [editedGroundQuotePrice, setEditedGroundQuotePrice] = useState<number>(serverGroundQuotePrice);

  // Prices can only change while the quote awaits review — lock them once it's approved/rejected (possibly elsewhere)
  const isPriceEditable = !isViewOnly && quote?.status === QuoteStatus.Created;

  // Locked quotes show server prices, so edits/approvals made elsewhere (e.g. NOP Nexus) appear without a reload
  const airQuotePrice = isPriceEditable ? editedAirQuotePrice : serverAirQuotePrice;
  const groundQuotePrice = isPriceEditable ? editedGroundQuotePrice : serverGroundQuotePrice;

  const [benchmark, setBenchmark] = useState<RoutePlanType | null>(null);

  const { data: benchmarkData } = useBenchmarkById(benchmarkId);

  useEffect(() => {
    const getBenchmarkPlan = async () => {
      if (benchmarkData && !benchmark) {
        const benchmarkPlan = await convertRoute({
          route: benchmarkData.plan,
          planType: RouteTypeSettingsEnum.Full,
          isTmdxDriveCompatible: !!logisticsProjectionSnapshot?.isTmdxDriveCompatible,
        });
        setBenchmark({ ...benchmarkPlan, locations: [...caseLocations, ...benchmarkPlan.locations] });
      }
    };

    getBenchmarkPlan();
  }, [caseLocations, benchmarkData, benchmark, convertRoute, logisticsProjectionSnapshot]);

  const aircraftsList = useMemo(() => {
    return (
      aircrafts?.map<BaseLegResourceType<AircraftLegResource>>((a) => ({
        id: a.resourceId,
        type: CaseResourceType.Aircraft,
        data: a.data,
      })) || []
    );
  }, [aircrafts]);

  const routeSchemaConfig = useMemo(() => {
    if (!mainRouteData) return null;

    const defaultSegments = getRouteSegments({
      donorHospital: donorHospital!,
      txCenter,
      legs: mainRouteData.legs,
      departureHub: getLocationById(mainRouteData.departureHub?.locationId) as HubRouteLocation,
      arrivalHub: getLocationById(mainRouteData.arrivalHub?.locationId) as HubRouteLocation,
    });

    const { segments: newSegments, legs: newLegs } = convertRouteList(defaultSegments, mainRouteData.legs);

    return {
      organType,
      segments: newSegments,
      legs: newLegs,
      aircrafts: aircraftsList,
      routeType: 'full',
    } as RouteSchemaType;
  }, [aircraftsList, organType, donorHospital, mainRouteData, txCenter, getLocationById]);

  const handleAirQuotePriceChange = (value: string) => {
    const currentQuotePrice = parseCurrencyStringUS(value) * 100;
    setEditedAirQuotePrice(currentQuotePrice);
  };

  const currencySymbol = getCurrencySymbol(quoteDetails.currency);
  const currencyFormatter = getCurrencyFormatter(quoteDetails.currency);

  useEffect(() => {
    setWarningMessages([]);
    setErrorMessages([]);

    const isEuropeanRegion = getIsEuropeanRegion(serviceRegionId);

    const getBenchmarkErrorText = () => {
      if (organType === OrganType.Kidney) {
        return 'Benchmark is not available for kidney cases. The Air quote has been set to zero. Please make sure to adjust the Air quote amount.';
      }
      if (isEuropeanRegion) {
        return 'Benchmark is not available for European countries. The Air quote has been set to zero. Please make sure to adjust the Air quote amount.';
      }
      return 'Benchmark generation failed. The Air quote has been set to zero.';
    };

    const benchmarkErrorText = getBenchmarkErrorText();

    if (
      quoteDetails.airQuote &&
      quoteDetails.airQuote.initPriceInCents === 0 &&
      quoteDetails.airQuote.finalPriceInCents === 0 &&
      !isViewOnly
    ) {
      setErrorMessages((prev) => [...prev, benchmarkErrorText]);
    }

    if (
      quoteDetails.airQuote &&
      quoteDetails.airQuote.initPriceInCents === 0 &&
      quoteDetails.airQuote.finalPriceInCents > 0 &&
      !isViewOnly
    ) {
      setWarningMessages((prev) => [
        ...prev,
        'Benchmark contains only Ground transportation. The minimum price of $15,800 has been used as the actual route contains TMDX or Supplemental Lift aviation.',
      ]);
    }

    if (quoteDetails.airQuote && !isViewOnly) {
      const calculatedAirPriceInCents = getCalculatedAirPriceInCents(quoteDetails.airQuote);

      if (calculatedAirPriceInCents > 0 && airQuotePrice < calculatedAirPriceInCents * 0.5) {
        setWarningMessages((prev) => [
          ...prev,
          'The manually entered quote amount is more than 50% below the calculated price. Please verify the amount before proceeding.',
        ]);
      }
    }

    if (quote && quote.status === QuoteStatus.Rejected && quote.rejectReason && quote.rejectReason !== null) {
      setErrorMessages((prev) => [
        ...prev,
        `Quote Rejected: ${caseQuoteRejectedReasonNames[quote.rejectReason!]} ${quote?.comment?.comment ? `(${quote?.comment?.comment})` : ''}`,
      ]);
    }
  }, [serviceRegionId, quoteDetails.airQuote, isViewOnly, quote, airQuotePrice, organType]);

  const onResetChanges = () => {
    setEditedAirQuotePrice(serverAirQuotePrice);
    setEditedGroundQuotePrice(serverGroundQuotePrice);
  };

  const handleApproveQuote = async () => {
    try {
      setIsLoading(true);

      const payload: QuoteApproveInternallyBody & { id: string } = {
        id,
        airQuotePriceInCents: quoteDetails.airQuote ? Math.round(airQuotePrice) : null,
        groundQuotePriceInCents: quoteDetails.groundQuote ? Math.round(groundQuotePrice) : null,
        comment,
      };

      await onApprove(payload);
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTXCApprove = async () => {
    try {
      setIsLoading(true);

      await onApproveTxCenterProvider?.(id, comment);
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectQuote = async () => {
    try {
      setIsRejectLoading(true);

      await onReject(id, comment);
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsRejectLoading(false);
    }
  };

  const getTotals = () => {
    const lightsSirensValue =
      quoteDetails.groundQuote && quoteDetails.groundQuote.lightsAndSirensInCents
        ? quoteDetails.groundQuote.lightsAndSirensInCents
        : 0;

    return currencyFormatter.format((airQuotePrice + groundQuotePrice + lightsSirensValue) / 100);
  };

  const logisticsComment = useMemo(() => {
    if (!routeComment || !routeComment.comment) return null;

    const dateTimeUtc = routeComment.createdAt
      ? parseZonedDateTime(getTzDateTimeString(routeComment.createdAt)).toAbsoluteString()
      : null;

    const formattedDateTime = dateTimeUtc
      ? formatDate(new Date(dateTimeUtc), 'MMM do, yyyy HH:mm zzz', {
          timezone: getLocalTimeZone(),
        })
      : '';

    return {
      comment: routeComment.comment,
      userName: routeComment.user
        ? getFullName(routeComment.user.firstName, routeComment.user.lastName)
        : 'Logistics Manager',
      dateString: formattedDateTime,
    };
  }, [routeComment]);

  const canApproveQuote = useMemo(() => {
    const hasGroundQuotePermission = checkPermission(RouteBuilderPermissions.ActionReviewGroundQuote);
    const hasAirQuotePermission = checkPermission(RouteBuilderPermissions.ActionReviewAirQuote);
    const isQuoteCreated = quote?.status === QuoteStatus.Created;
    const isAirQuote = quoteDetails && !!quoteDetails.airQuote;
    const hasPermission = isAirQuote ? hasAirQuotePermission : hasGroundQuotePermission;

    return hasPermission && isQuoteCreated;
  }, [checkPermission, quote, quoteDetails]);

  const hasLogisticsActions = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionSendQuote);
    const isQuoteRejected = quote?.status === QuoteStatus.Rejected;

    return hasPermission && isQuoteRejected;
  }, [quote, checkPermission]);

  const getTransportProviders = () => {
    const transportProviders: string[] = [];

    if (!mainRouteData) return transportProviders;

    const hasTmdxAviaLeg = mainRouteData.legs.some((l) => l.legType === RouteLegTypeEnum.TmdxJetAviaLeg);
    const hasSupplementalLiftLeg = mainRouteData.legs.some(
      (l) => l.legType === RouteLegTypeEnum.SupplementalLiftAviaLeg,
    );

    if (hasTmdxAviaLeg) {
      transportProviders.push('TMDX');
    }

    if (hasSupplementalLiftLeg) {
      transportProviders.push('SLP');
    }

    return (
      <div className="p-3 pb-0 flex justify-between">
        <div className="flex">
          <span className="text-md font-semibold">{uniq(transportProviders).join(' / ')}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      {canApproveQuote && !!logisticsComment && (
        <div className="w-full flex flex-col gap-1 bg-base-90 py-2 px-3 rounded-lg mb-6">
          <span className="text-m-sb text-base-5">{`"${logisticsComment.comment}"`}</span>
          <div className="flex gap-2 items-center">
            <div className="flex gap-1 items-center">
              <TmdxAvatar size="sm" type="user" />
              <span className="text-s-m tet-base-5">{logisticsComment.userName}</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-base-10" />
            {!!logisticsComment.dateString && (
              <span className="text-s-r text-base-20">{logisticsComment.dateString}</span>
            )}
          </div>
        </div>
      )}

      {quote && quote.status === QuoteStatus.Recalled && quote.recallComment && (
        <InfoBanner iconType="cross">
          <span className="whitespace-pre-wrap break-all">{`Quote Recalled. ${quote.recallComment.comment}`}</span>
        </InfoBanner>
      )}

      {reAllocationCaseId && (
        <InfoBanner>
          Reallocation from <LinkToCase id={reAllocationCaseId} />
        </InfoBanner>
      )}

      <div className="relative flex flex-col items-center gap-2 w-full">
        <HospitalsBlock
          donorHospital={donorHospital!}
          txCenter={txCenter}
          deviceTracking={deviceTracking}
          donorHospitalDate={donorHospitalDate}
          donorHospitalTime={donorHospitalTime}
          donorHospitalTimeZone={donorHospitalTimeZone}
          recipientHospitalDate={recipientHospitalDate}
          recipientHospitalTime={recipientHospitalTime}
          recipientHospitalTimeZone={recipientHospitalTimeZone}
          txEtaHospitalDate={txEtaHospitalDate}
          txEtaHospitalTime={txEtaHospitalTime}
        />
      </div>

      {benchmark && (
        <div className="bg-white flex flex-col mt-2">
          <div className="p-3 pb-0 flex justify-between">
            <div className="flex">
              <span className="text-md font-semibold">Benchmark</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span>Block hours: </span>
              {benchmark.metrics.totalFlightDuration > 0 && (
                <>
                  <AircraftIcon height={16} width={16} />
                  <span>
                    {durationInSecondsToHours(
                      benchmark.metrics.totalFlightDuration + benchmark.metrics.totalBlockDuration,
                    )}
                    h
                  </span>
                </>
              )}
              {benchmark.metrics.totalDriveDuration > 0 && (
                <>
                  <CarGroundIcon height={16} width={16} />
                  <span>{durationInSecondsToHours(benchmark.metrics.totalDriveDuration)}h</span>
                </>
              )}
            </div>
          </div>
          <div className="px-3">
            <RouteSchema
              key={benchmark.id}
              organType={organType}
              segments={benchmark.segments}
              legs={benchmark.legs}
              aircrafts={benchmark.resources[CaseResourceType.Aircraft]}
              locations={benchmark.locations}
            />
          </div>
        </div>
      )}

      {routeSchemaConfig && (
        <div className="bg-white flex flex-col mt-2">
          <div className="flex items-center gap-2 text-sm font-semibold">{getTransportProviders()}</div>
          <div className="px-3">
            <RouteSchema {...routeSchemaConfig} locations={list} />
          </div>
          <div className="pt-2">
            <LogisticsResourcesList resources={resourcesList} donorHospital={donorHospital} disabled />
          </div>
        </div>
      )}

      <div className="mt-2">
        <div>
          <div className="flex flex-row justify-between">
            <h1 className="text-hm">Quote</h1>
            <div className="flex flex-row">
              {isPriceEditable && (
                <Button size="sm" variant="light" className="border-0" onPress={onResetChanges}>
                  <IoClose size={20} />
                  <span className="text-sm font-bold">Reset changes</span>
                </Button>
              )}
              {isViewOnly && quote && (
                <TmdxChip color={dangerQuoteStatuses.includes(quote.status) ? 'danger' : 'secondary'}>
                  {quoteStatusNames[quote.status]}
                </TmdxChip>
              )}
            </div>
          </div>

          <hr className="border-t-1 mt-3 mb-3 border-red" />

          {desyncedState && (
            <InfoBanner iconType="warning">
              This quote can no longer be approved, as the route was modified while the quote was being generated.
              Decline the quote to unblock the case.
            </InfoBanner>
          )}

          <div className="mt-2 mb-2">
            {warningMessages.map((m) => (
              <InfoBanner iconType="warning">{m}</InfoBanner>
            ))}
            {errorMessages.map((m) => (
              <InfoBanner iconType="cross">{m}</InfoBanner>
            ))}
          </div>

          {/* Leave until final decision for this checkbox */}
          {/* {canViewExcludeDryRunDiscount && (
            <Checkbox
              size="sm"
              isSelected={excludeDryRunDiscount}
              isDisabled={!canApproveQuote || isViewOnly || recallQuote}
              onValueChange={setExcludeDryRunDiscount}
            >
              Exclude {donorType} Dry Run Discount
            </Checkbox>
          )} */}

          <div className="flex flex-col gap-4 mb-5">
            {quoteDetails.airQuote && (
              <div className="flex w-full flex-row items-center gap-3">
                <div className="flex w-4 shrink-0 flex-col">
                  <AircraftIcon height={16} width={16} />
                </div>
                <div className="flex w-[190px] shrink-0 flex-col">
                  {(quoteDetails.airQuote.routeTmdxDurationInSeconds > 0 ||
                    quoteDetails.airQuote.routeSplDurationInSeconds > 0) && (
                    <span className="text-xs text-base-20">Flight hours</span>
                  )}
                  {quoteDetails.airQuote.routeTmdxDurationInSeconds > 0 && (
                    <span className="whitespace-nowrap font-bold text-base-3 text-sm">
                      {durationInSecondsToHours(quoteDetails.airQuote.routeTmdxDurationInSeconds)}h TMDX
                    </span>
                  )}
                  {quoteDetails.airQuote.routeSplDurationInSeconds > 0 && (
                    <span className="whitespace-nowrap font-bold text-base-3 text-sm">
                      {durationInSecondsToHours(quoteDetails.airQuote.routeSplDurationInSeconds)}h SLP
                    </span>
                  )}
                </div>
                <div className="flex w-[220px] shrink-0 flex-col">
                  <NumericFormat
                    prefix={currencySymbol}
                    thousandSeparator
                    decimalScale={2}
                    customInput={Input}
                    value={airQuotePrice / 100}
                    onValueChange={(values) => handleAirQuotePriceChange(values.value)}
                    classNames={{
                      inputWrapper: 'bg-base-60! rounded-lg',
                      input: 'text-lg font-bold',
                    }}
                    isDisabled={!isPriceEditable}
                    allowNegative={false}
                  />
                </div>
                <div className="flex flex-col flex-1">
                  {(quoteDetails.airQuote.initPriceInCents > 0 || quoteDetails.airQuote.finalPriceInCents > 0) && (
                    <div>
                      <span className="text-xs text-base-20">Calculated price: </span>
                      <span className="text-xs text-base-20 font-bold">
                        {currencyFormatter.format(getCalculatedAirPriceInCents(quoteDetails.airQuote) / 100)}
                      </span>
                    </div>
                  )}
                  {quoteDetails.airQuote.supplementalLiftPriceInCents > 0 && (
                    <div>
                      <span className="text-xs text-base-20">Supplemental Lift Provider: </span>
                      <span className="text-xs text-base-20 font-bold">
                        {currencyFormatter.format(quoteDetails.airQuote.supplementalLiftPriceInCents / 100)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {quoteDetails.groundQuote && (
              <div className="flex w-full flex-row items-center gap-3">
                <div className="flex w-4 shrink-0 flex-col">
                  <CarGroundIcon height={16} width={16} />
                </div>
                <div className="flex w-[190px] shrink-0 flex-col">
                  {(quoteDetails.groundQuote.durationInSeconds > 0 ||
                    (quoteDetails.groundQuote.selfServicedDurationInSeconds ?? 0) > 0) && (
                    <span className="text-xs text-base-20">Drive hours</span>
                  )}
                  {quoteDetails.groundQuote.durationInSeconds > 0 && (
                    <span className="whitespace-nowrap font-bold text-base-3 text-sm">
                      {durationInSecondsToHours(quoteDetails.groundQuote.durationInSeconds)}h TMDX Affiliate
                    </span>
                  )}
                  {(quoteDetails.groundQuote.selfServicedDurationInSeconds ?? 0) > 0 && (
                    <span className="whitespace-nowrap font-bold text-base-3 text-sm">
                      {durationInSecondsToHours(quoteDetails.groundQuote.selfServicedDurationInSeconds ?? 0)}h{' '}
                      Self-Serviced
                    </span>
                  )}
                  <span className="text-base-10 text-xs">{currencyFormatter.format(185)}/h</span>
                </div>
                <div className="flex w-[220px] shrink-0 flex-col">
                  <NumericFormat
                    prefix={currencySymbol}
                    thousandSeparator
                    decimalScale={2}
                    customInput={Input}
                    value={groundQuotePrice / 100}
                    onValueChange={(values) =>
                      setEditedGroundQuotePrice(values?.floatValue ? values.floatValue * 100 : 0)
                    }
                    classNames={{
                      inputWrapper: 'bg-base-60! rounded-lg',
                      input: 'text-lg font-bold',
                    }}
                    isDisabled={!isPriceEditable}
                    allowNegative={false}
                  />
                </div>
                <div className="flex flex-1 flex-col justify-center">
                  <div className="flex flex-row">
                    <span className="text-xs text-base-20 mr-1">Un-capped amount:</span>
                    <span className="text-xs text-base-20 font-bold">
                      {quoteDetails.groundQuote.initPriceInCents
                        ? currencyFormatter.format(quoteDetails.groundQuote.initPriceInCents / 100)
                        : currencyFormatter.format(0)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {quoteDetails.groundQuote && quoteDetails.groundQuote.lightsAndSirensInCents && (
              <div className="flex w-full flex-row items-center gap-3">
                <div className="flex w-4 shrink-0 flex-col">
                  <LightsAndSirens height={16} width={16} />
                </div>
                <div className="flex w-[190px] shrink-0 flex-col">
                  <span className="text-xs text-base-20">Lights and Sirens</span>
                  <span className="whitespace-nowrap font-bold text-base-3 text-sm">
                    {currencyFormatter.format(quoteDetails.groundQuote.lightsAndSirensInCents / 100)}
                  </span>
                </div>
                <div className="flex w-[220px] shrink-0 flex-col">
                  <NumericFormat
                    prefix={currencySymbol}
                    thousandSeparator
                    decimalScale={2}
                    customInput={Input}
                    value={quoteDetails.groundQuote.lightsAndSirensInCents / 100}
                    isReadOnly
                    classNames={{
                      inputWrapper: 'bg-base-60! rounded-lg',
                      input: 'text-lg font-bold',
                    }}
                    isDisabled={!isPriceEditable}
                    allowNegative={false}
                  />
                </div>
                <div className="flex flex-1 flex-col justify-center" />
              </div>
            )}

            <div className="flex flex-row items-center gap-5">
              <div className="flex flex-col">
                <span className="text-xs text-base-20">Total</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold">{getTotals()}</span>
              </div>
            </div>
          </div>
        </div>

        <h1 className="text-hm">Comment</h1>
        <hr className="border-t-1 mt-3 mb-3 border-red" />
        <Input
          value={comment || ''}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          classNames={{
            inputWrapper: 'bg-base-60! rounded-lg',
          }}
          isDisabled={isViewOnly}
        />
        <hr className="border-t-1 mt-3 border-base-60" />
      </div>

      {hasLogisticsActions && onApproveTxCenterProvider && !isViewOnly && (
        <div className="flex self-end gap-2 items-center">
          {quote?.rejectReason && quote.rejectReason !== QuoteRejectReason.NotSuitable && (
            <Button
              size="sm"
              variant="light"
              className="border-1 border-red text-red"
              isLoading={isLoading}
              onPress={handleTXCApprove}
            >
              Approve TXC Provider
            </Button>
          )}
          <Button size="sm" onPress={onCloseModal}>
            Proceed to case closure
          </Button>
        </div>
      )}

      {canApproveQuote && !isViewOnly && (
        <div className="flex self-end gap-2 items-center">
          <Button
            isLoading={isRejectLoading}
            isDisabled={isLoading}
            size="sm"
            variant="light"
            className="border-1 border-red text-red"
            onPress={handleRejectQuote}
          >
            Decline
          </Button>
          {!desyncedState && (
            <Button size="sm" onPress={handleApproveQuote} isLoading={isLoading} isDisabled={isRejectLoading}>
              Approve
            </Button>
          )}
        </div>
      )}

      {recallQuote && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            className="border-danger-40 text-danger-40 h-9 px-3"
            onPress={() => onOpenModal({ type: RouteModalTypes.QuoteRecall, context: { onRecall } })}
          >
            Recall Quote
          </Button>
        </div>
      )}
    </>
  );
};
